import { ServiceProviderOption } from '@acp/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getComplianceAgencyServiceProviders } from '../service';

interface UseServiceProvidersResult {
    options: ServiceProviderOption[];
    isLoading: boolean;
    refetch: () => void; // re-fetch after creating a new provider
}

async function fetchServiceProviders(): Promise<ServiceProviderOption[]> {
    const data = await getComplianceAgencyServiceProviders();
    return data.map((p) => ({
        label: p.supplier_name,
        value: { id: p.id, providedBy: p.supplier_name },
    }));
}

export const SERVICE_PROVIDERS_QUERY_KEY = 'service-providers';

export function useServiceProviders(
    complianceAgencyId: number | undefined
): UseServiceProvidersResult {
    const query = useQuery({
        queryKey: [SERVICE_PROVIDERS_QUERY_KEY, { complianceAgencyId }],
        queryFn: () => fetchServiceProviders(),
        // We want fresh list each time edit mode is entered; avoid stale cache persistence
        staleTime: 0,
        refetchOnWindowFocus: true,
    });

    return {
        options: query.data ?? [],
        isLoading: query.isLoading || query.isFetching,
        refetch: () => { void query.refetch(); },
    };
}

export function useRefreshServiceProviders() {
    const queryClient = useQueryClient();

    const refreshServiceProviders = async () => {
        // Invalidate all service provider queries
        await queryClient.invalidateQueries({
            queryKey: [SERVICE_PROVIDERS_QUERY_KEY],
        });
    };

    return refreshServiceProviders;
}
